import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  Folder,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  Check,
  Search,
  X,
  Layers,
} from "lucide-react";

/**
 * Builds a category tree, maps, and cycle prevention.
 */
function processCategories(rawCategories = [], excludeId = null) {
  const categories = Array.isArray(rawCategories)
    ? rawCategories
    : Array.isArray(rawCategories?.data)
      ? rawCategories.data
      : [];
  const normalizedExcludeId =
    excludeId != null && excludeId !== "" ? String(excludeId) : null;

  const categoryMap = new Map();
  categories.forEach((cat) => {
    if (!cat || cat.id == null) return;
    const id = String(cat.id);
    categoryMap.set(id, {
      ...cat,
      id: cat.id,
      name: cat.name || "Unnamed Category",
      parentId:
        cat.parent_id != null
          ? String(cat.parent_id)
          : cat.parent?.id != null
            ? String(cat.parent.id)
            : null,
      children: [],
    });
  });

  // Identify all descendant IDs of excludeId to prevent circular parent selection
  const excludedIds = new Set();
  if (normalizedExcludeId) {
    excludedIds.add(normalizedExcludeId);
    const collectDescendants = (parentId) => {
      categoryMap.forEach((node) => {
        if (node.parentId === parentId && !excludedIds.has(String(node.id))) {
          excludedIds.add(String(node.id));
          collectDescendants(String(node.id));
        }
      });
    };
    collectDescendants(normalizedExcludeId);
  }

  // Build tree from valid (non-excluded) categories
  const roots = [];
  categoryMap.forEach((node, id) => {
    if (excludedIds.has(id)) return;

    if (node.parentId && categoryMap.has(node.parentId)) {
      if (!excludedIds.has(node.parentId)) {
        categoryMap.get(node.parentId).children.push(node);
      } else {
        roots.push(node);
      }
    } else {
      roots.push(node);
    }
  });

  // Compute full paths
  const paths = new Map();
  const buildPaths = (nodes, currentPath = []) => {
    nodes.forEach((node) => {
      const p = [...currentPath, node.name];
      paths.set(String(node.id), p.join(" > "));
      if (node.children?.length) {
        buildPaths(node.children, p);
      }
    });
  };
  buildPaths(roots);

  return { tree: roots, categoryMap, paths };
}

/**
 * Helper to collect selected category (if it has children) and all its ancestors
 */
function getSelectedExpandIds(selectedVal, categoryMap) {
  const ids = new Set();
  if (selectedVal != null && selectedVal !== "" && categoryMap) {
    let curr = categoryMap.get(String(selectedVal));
    if (curr) {
      if (curr.children?.length) {
        ids.add(String(curr.id));
      }
      while (curr && curr.parentId) {
        ids.add(String(curr.parentId));
        curr = categoryMap.get(String(curr.parentId));
      }
    }
  }
  return ids;
}

/**
 * Recursive Tree Node component for selecting parent category
 */
const TreeNode = ({
  node,
  selectedId,
  onSelect,
  expandedIds,
  onToggleExpand,
  searchTerm,
  depth = 0,
}) => {
  const isSelected =
    selectedId != null &&
    selectedId !== "" &&
    String(selectedId) === String(node.id);
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = searchTerm ? true : expandedIds.has(String(node.id));

  // Check search matching (self or any child)
  const matchesSearch =
    !searchTerm ||
    node.name.toLowerCase().includes(searchTerm.toLowerCase());

  const hasMatchingDescendant = (n) => {
    if (!searchTerm) return true;
    if (n.name.toLowerCase().includes(searchTerm.toLowerCase())) return true;
    return (n.children || []).some(hasMatchingDescendant);
  };

  if (searchTerm && !matchesSearch && !hasMatchingDescendant(node)) {
    return null;
  }

  return (
    <div className="select-none">
      <div
        className={cn(
          "flex items-center gap-2 py-1.5 px-2 rounded-md text-sm transition-colors cursor-pointer group my-0.5",
          isSelected
            ? "bg-primary/15 text-primary font-semibold border border-primary/40 shadow-xs"
            : "hover:bg-muted/70 text-foreground",
        )}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
        onClick={() => onSelect(node.id)}
      >
        {hasChildren ? (
          <button
            type="button"
            className="p-1 -ml-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground shrink-0 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(String(node.id));
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <div className="w-5 shrink-0" />
        )}

        {hasChildren && isExpanded ? (
          <FolderOpen className="h-4 w-4 text-primary shrink-0" />
        ) : (
          <Folder className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0 transition-colors" />
        )}

        <span className="truncate flex-1 text-sm">{node.name}</span>

        {hasChildren && (
          <span className="text-[11px] text-muted-foreground px-1.5 py-0.2 rounded bg-muted/80 shrink-0">
            {node.children.length}
          </span>
        )}

        {isSelected && (
          <Badge variant="default" className="text-[10px] h-5 ml-1 gap-1 shrink-0">
            <Check className="h-3 w-3" /> Selected
          </Badge>
        )}
      </div>

      {hasChildren && isExpanded && (
        <div className="border-l border-border/60 ml-4.5 pl-0.5">
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              searchTerm={searchTerm}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

TreeNode.propTypes = {
  node: PropTypes.object.isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelect: PropTypes.func.isRequired,
  expandedIds: PropTypes.instanceOf(Set).isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  searchTerm: PropTypes.string,
  depth: PropTypes.number,
};

/**
 * ParentCategorySelect:
 * Clear, intuitive category tree UI for selecting parent category.
 * Embedded directly without complicated toggles or nested dropdowns.
 */
const ParentCategorySelect = ({
  value,
  onChange,
  categories = [],
  excludeId = null,
  className,
  isLoading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  // Process categories into hierarchy tree
  const { tree, categoryMap, paths } = useMemo(() => {
    return processCategories(categories, excludeId);
  }, [categories, excludeId]);

  const [expandedIds, setExpandedIds] = useState(() =>
    getSelectedExpandIds(value, categoryMap),
  );

  const prevExcludeIdRef = useRef(excludeId);

  // Expand ancestors and self of selected category when value changes or categories load
  useEffect(() => {
    const isNewCategory = prevExcludeIdRef.current !== excludeId;
    prevExcludeIdRef.current = excludeId;

    if (value != null && value !== "") {
      const toExpand = getSelectedExpandIds(value, categoryMap);
      if (isNewCategory) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setExpandedIds(toExpand);
      } else if (toExpand.size > 0) {
        setExpandedIds((prev) => new Set([...prev, ...toExpand]));
      }
    } else if (isNewCategory) {
      setExpandedIds(new Set());
    }
  }, [value, categoryMap, excludeId]);

  const toggleExpand = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    const allIds = new Set();
    const collect = (nodes) => {
      nodes.forEach((n) => {
        if (n.children?.length) {
          allIds.add(String(n.id));
          collect(n.children);
        }
      });
    };
    collect(tree);
    setExpandedIds(allIds);
  }, [tree]);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  const handleSelect = (id) => {
    const finalVal = id == null || id === "" ? null : Number(id);
    const selectedCat = id ? categoryMap.get(String(id)) : null;
    onChange(finalVal, selectedCat || null);
  };

  const isRootSelected = value == null || value === "";
  const selectedPath = value ? paths.get(String(value)) : null;
  const selectedCat = value ? categoryMap.get(String(value)) : null;

  return (
    <div className={cn("space-y-2", className)}>
      {/* Selected Parent Status Banner */}
      <div className="flex items-center justify-between p-2 rounded-lg bg-muted/40 border text-xs">
        <span className="text-muted-foreground font-medium">Selected Parent Category:</span>
        {isRootSelected ? (
          <Badge variant="outline" className="gap-1 font-medium bg-background text-foreground/80">
            <Layers className="h-3 w-3 text-primary" />
            None
          </Badge>
        ) : (
          <div className="flex items-center gap-1.5 max-w-[70%]">
            <Badge variant="secondary" className="gap-1 text-xs truncate max-w-full font-medium">
              <Folder className="h-3 w-3 text-primary shrink-0" />
              <span className="truncate">{selectedPath || selectedCat?.name || `#${value}`}</span>
            </Badge>
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors"
              title="Remove parent (set as Root category)"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Category Tree Box */}
      <div className="border rounded-xl bg-card p-3 space-y-2.5 shadow-2xs">
        {/* Search & Expand/Collapse Toolbar */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 pl-8 text-xs bg-background"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-[11px] px-2"
              onClick={expandAll}
            >
              Expand All
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-[11px] px-2"
              onClick={collapseAll}
            >
              Collapse
            </Button>
          </div>
        </div>

        {/* Scrollable Tree Container */}
        <div className="max-h-[200px] overflow-y-auto space-y-0.5 border rounded-lg p-2 bg-background/60">
          {/* Tree nodes */}
          {isLoading ? (
            <div className="space-y-2 p-2 animate-pulse">
              <div className="flex items-center gap-2">
                <Skeleton className="h-3.5 w-3.5 rounded" />
                <Skeleton className="h-3.5 w-28 rounded" />
              </div>
              <div className="flex items-center gap-2 pl-4">
                <Skeleton className="h-3.5 w-3.5 rounded" />
                <Skeleton className="h-3.5 w-20 rounded" />
              </div>
              <div className="flex items-center gap-2 pl-4">
                <Skeleton className="h-3.5 w-3.5 rounded" />
                <Skeleton className="h-3.5 w-32 rounded" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-3.5 w-3.5 rounded" />
                <Skeleton className="h-3.5 w-24 rounded" />
              </div>
            </div>
          ) : tree.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">
              No categories available
            </p>
          ) : (
            tree.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                selectedId={value}
                onSelect={handleSelect}
                expandedIds={expandedIds}
                onToggleExpand={toggleExpand}
                searchTerm={searchTerm}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

ParentCategorySelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  categories: PropTypes.array,
  excludeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default ParentCategorySelect;
